import { createTheme } from "@mui/material/styles";
import { UI_RADIUS } from "./constants";
import {
  brandColorAlpha,
  getBrandContrastText,
  mixBrandColor,
  normalizeTenantBranding,
} from "./branding";
import type { TenantBrandingInput } from "./branding";

// --- TYPESCRIPT AUGMENTATION ---
// This tells TS that 'soft' and 'gradient' are valid variants
declare module "@mui/material/Button" {
  interface ButtonPropsVariantOverrides {
    soft: true;
    gradient: true;
  }
}

declare module "@mui/material/Chip" {
  interface ChipPropsVariantOverrides {
    soft: true;
  }
}
// -------------------------------

const darkBronzeText = "#F2C69B";
const darkSurface = "#181411";
const darkSurfaceElevated = "#211B16";

export type NextMocaThemeMode = "light" | "dark";

export const createNextMocaTheme = (
  mode: NextMocaThemeMode = "light",
  tenantBranding?: TenantBrandingInput,
) => {
  const isDark = mode === "dark";
  const branding = normalizeTenantBranding(tenantBranding);
  const primaryMain = branding.primaryColor;
  const primaryLight = mixBrandColor(primaryMain, "#FFFFFF", 0.28);
  const primaryDark = mixBrandColor(primaryMain, "#000000", 0.24);
  const secondaryMain = branding.secondaryColor;
  const accentMain = branding.accentColor;
  const brandPrimary = {
    main: primaryMain,
    light: primaryLight,
    dark: primaryDark,
    contrastText: getBrandContrastText(primaryMain),
    softSubtle: brandColorAlpha(primaryMain, 0.08),
    softHover: brandColorAlpha(primaryMain, 0.12),
    softStrong: brandColorAlpha(primaryMain, 0.25),
    focusRing: brandColorAlpha(primaryMain, 0.15),
  };
  const bronzeGradient = `linear-gradient(90deg, ${brandPrimary.light} 0%, ${brandPrimary.dark} 100%)`;
  const bronze = brandPrimary.main;
  const softBronze = brandPrimary.softSubtle;
  const softBronzeHover = brandPrimary.softHover;
  const darkPrimaryMain = mixBrandColor(primaryMain, "#FFFFFF", 0.18);
  const darkPrimaryLight = mixBrandColor(primaryMain, "#FFFFFF", 0.36);
  const darkPrimaryDark = mixBrandColor(primaryMain, "#000000", 0.18);

  return createTheme({
    palette: {
      mode,
      primary: {
        main: isDark ? darkPrimaryMain : brandPrimary.main,
        light: isDark ? darkPrimaryLight : brandPrimary.light,
        dark: isDark ? darkPrimaryDark : brandPrimary.dark,
        contrastText: isDark ? getBrandContrastText(darkPrimaryMain) : brandPrimary.contrastText,
      },
      secondary: {
        main: secondaryMain,
        light: mixBrandColor(secondaryMain, "#FFFFFF", 0.28),
        dark: mixBrandColor(secondaryMain, "#000000", 0.24),
        contrastText: getBrandContrastText(secondaryMain),
      },
      info: {
        main: accentMain,
        light: mixBrandColor(accentMain, "#FFFFFF", 0.28),
        dark: mixBrandColor(accentMain, "#000000", 0.24),
        contrastText: getBrandContrastText(accentMain),
      },
      background: {
        default: isDark ? "#0F0D0B" : "#FBFAF8",
        paper: isDark ? darkSurface : "#FFFFFF",
      },
      text: {
        primary: isDark ? "#FFF7EF" : "#1D1D1F",
        secondary: isDark ? "#D9C9BA" : "#6F6A65",
      },
      action: {
        hover: isDark ? "rgba(224, 177, 132, 0.12)" : brandPrimary.softSubtle,
        selected: isDark ? "rgba(224, 177, 132, 0.18)" : brandPrimary.softHover,
        disabled: isDark ? "rgba(255, 247, 239, 0.30)" : "rgba(0, 0, 0, 0.26)",
        disabledBackground: isDark ? "rgba(255, 247, 239, 0.10)" : "rgba(0, 0, 0, 0.12)",
      },
      divider: isDark ? "rgba(224, 177, 132, 0.24)" : "#E8D8CA",
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          html: {
            backgroundColor: isDark ? "#0F0D0B" : "#FBFAF8",
          },
          "#root": {
            minHeight: "100vh",
            backgroundColor: isDark ? "#0F0D0B" : "#FBFAF8",
            color: isDark ? "#FFF7EF" : "#1D1D1F",
          },
          body: {
            backgroundColor: isDark ? "#0F0D0B" : "#FBFAF8",
            color: isDark ? "#FFF7EF" : "#1D1D1F",
          },
          ...(isDark && {
            ".MuiContainer-root, main": {
              color: "#FFF7EF",
            },
            ".MuiPaper-root:not(.MuiAppBar-root):not(.MuiDrawer-paper), .MuiCard-root": {
              backgroundColor: `${darkSurface} !important`,
              backgroundImage: "none !important",
              color: "#FFF7EF !important",
              borderColor: "rgba(224,177,132,0.22) !important",
            },
            ".MuiPaper-root .MuiPaper-root, .MuiCard-root .MuiPaper-root": {
              backgroundColor: `${darkSurfaceElevated} !important`,
            },
            ".MuiTypography-root": {
              color: "inherit",
            },
            ".MuiTypography-colorTextSecondary, .MuiTypography-root.MuiTypography-colorTextSecondary": {
              color: "#D9C9BA !important",
            },
            ".MuiTableHead-root .MuiTableRow-root, .MuiTableCell-head": {
              backgroundColor: "rgba(224,177,132,0.12) !important",
              color: "#F2C69B !important",
            },
            ".MuiTableCell-root": {
              color: "#FFF7EF !important",
              borderBottomColor: "rgba(224,177,132,0.16) !important",
            },
            ".MuiTableRow-root:hover": {
              backgroundColor: "rgba(224,177,132,0.08) !important",
            },
            ".MuiFormLabel-root, .MuiInputLabel-root": {
              color: "#D9C9BA !important",
            },
            ".MuiSvgIcon-root": {
              color: "inherit",
            },
            "*, *::before, *::after": {
              scrollbarColor: "#6E4B31 #17120F",
            },
            "::-webkit-scrollbar": {
              width: 10,
              height: 10,
            },
            "::-webkit-scrollbar-track": {
              background: "#17120F",
            },
            "::-webkit-scrollbar-thumb": {
              background: "#6E4B31",
              borderRadius: 999,
              border: "2px solid #17120F",
            },
          }),
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: UI_RADIUS.button,
            textTransform: "none",
          },
        },
        variants: [
          {
            props: { variant: "gradient" },
            style: {
              background: bronzeGradient,
              color: "#ffffff",
              transition: "transform 0.2s",
              "&:hover": {
                transform: "translateY(-1px)",
                opacity: 0.95,
              },
            },
          },
          {
            props: { variant: "soft", color: "primary" },
            style: {
              backgroundColor: isDark ? "rgba(201, 147, 100, 0.18)" : brandPrimary.softHover,
              color: isDark ? darkBronzeText : brandPrimary.dark,
              "&:hover": {
                backgroundColor: isDark ? "rgba(201, 147, 100, 0.28)" : brandPrimary.softStrong,
              },
            },
          },
        ],
      },
      MuiTextField: {
        defaultProps: {
          variant: "filled",
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            ...(isDark && {
              backgroundImage: "none",
              color: "#FFF7EF",
            }),
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            ...(isDark && {
              backgroundColor: darkSurface,
              backgroundImage: "none",
              borderColor: "rgba(224,177,132,0.22)",
              color: "#FFF7EF",
              boxShadow: "0 18px 54px rgba(0,0,0,0.36)",
            }),
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            ...(isDark && {
              backgroundColor: darkSurface,
              backgroundImage: "none",
              color: "#FFF7EF",
              borderColor: "rgba(224,177,132,0.22)",
            }),
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            ...(isDark && {
              backgroundColor: darkSurface,
              backgroundImage: "none",
              color: "#FFF7EF",
              border: "1px solid rgba(224,177,132,0.22)",
            }),
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            ...(isDark && {
              backgroundColor: darkSurfaceElevated,
              backgroundImage: "none",
              color: "#FFF7EF",
              border: "1px solid rgba(224,177,132,0.20)",
            }),
          },
        },
      },
      MuiPopover: {
        styleOverrides: {
          paper: {
            ...(isDark && {
              backgroundColor: darkSurfaceElevated,
              backgroundImage: "none",
              color: "#FFF7EF",
              border: "1px solid rgba(224,177,132,0.20)",
            }),
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            ...(isDark && {
              color: "#FFF7EF",
              borderBottomColor: "rgba(224,177,132,0.16)",
            }),
          },
          head: {
            ...(isDark && {
              color: "#F2C69B",
              backgroundColor: "rgba(224,177,132,0.08)",
            }),
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            ...(isDark && {
              "&:hover": {
                backgroundColor: "rgba(224,177,132,0.08)",
              },
            }),
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            ...(isDark && {
              border: "1px solid rgba(224,177,132,0.18)",
            }),
          },
          standardInfo: {
            ...(isDark && {
              backgroundColor: "rgba(76, 154, 214, 0.14)",
              color: "#D7ECFF",
            }),
          },
          standardSuccess: {
            ...(isDark && {
              backgroundColor: "rgba(91, 160, 92, 0.16)",
              color: "#DDF6DD",
            }),
          },
          standardWarning: {
            ...(isDark && {
              backgroundColor: "rgba(255, 184, 89, 0.16)",
              color: "#FFE2BD",
            }),
          },
          standardError: {
            ...(isDark && {
              backgroundColor: "rgba(239, 99, 99, 0.16)",
              color: "#FFDADA",
            }),
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            ...(isDark && {
              color: "#FFF7EF",
              "&.Mui-selected": {
                backgroundColor: "rgba(224,177,132,0.16)",
              },
              "&.Mui-selected:hover, &:hover": {
                backgroundColor: "rgba(224,177,132,0.12)",
              },
            }),
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            ...(isDark && {
              color: "#D9C9BA",
              "&:hover": {
                backgroundColor: "rgba(224,177,132,0.10)",
              },
            }),
          },
        },
      },
      MuiFilledInput: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? "rgba(255,247,239,0.08)" : softBronze,
            color: isDark ? "#FFF7EF" : undefined,
            borderRadius: UI_RADIUS.input,
            border: isDark ? "1px solid rgba(224,177,132,0.18)" : "1px solid transparent",
            transition: "all 0.2s ease-in-out",
            "&:before, &:after": { display: "none" },
            "&:hover": {
              backgroundColor: isDark ? "rgba(255,255,255,0.09)" : softBronzeHover,
            },
            "&.Mui-focused": {
              backgroundColor: isDark ? darkSurfaceElevated : "#ffffff",
              borderColor: isDark ? "#D1935C" : bronze,
              boxShadow: `0 0 0 4px ${brandPrimary.focusRing}`,
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: UI_RADIUS.input,
            ...(isDark && {
              backgroundColor: "rgba(255,247,239,0.07)",
              color: "#FFF7EF",
              "& fieldset": { borderColor: "rgba(224,177,132,0.22)" },
              "&:hover fieldset": { borderColor: "rgba(224,177,132,0.40)" },
              "&.Mui-focused fieldset": { borderColor: "#D1935C" },
            }),
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: UI_RADIUS.chip,
            ...(isDark && {
              color: "#FFF7EF",
              borderColor: "rgba(224,177,132,0.26)",
            }),
          },
        },
        variants: [
          {
            props: { variant: "soft", color: "primary" },
            style: {
              backgroundColor: isDark ? "rgba(201, 147, 100, 0.18)" : brandPrimary.softHover,
              color: isDark ? darkBronzeText : brandPrimary.dark,
              fontWeight: 600,
            },
          },
        ],
      },
    },
  });
};

const theme = createNextMocaTheme("light");

export default theme;
